import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import "../styles/asyncActionButton.css";

export default function AsyncActionButton({
  onAction,
  children,
  busyText,
  errorText,
  externalBusy = false,
  compact = false,
  disabled = false,
  className = "",
  type = "button",
  errorDuration = 2600,
  ...buttonProps
}) {
  const { t } = useLanguage();
  const [status, setStatus] = useState("idle");
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const resetTimerRef = useRef(null);
  const busy = externalBusy || status === "busy";

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (resetTimerRef.current) window.clearTimeout(resetTimerRef.current);
    };
  }, []);

  const run = async (event) => {
    if (disabled || externalBusy || inFlightRef.current) return;
    inFlightRef.current = true;
    setStatus("busy");
    try {
      const result = await onAction?.(event);
      if (result === false) throw new Error("ACTION_FAILED");
      if (mountedRef.current) setStatus("idle");
    } catch (_) {
      if (!mountedRef.current) return;
      setStatus("error");
      if (resetTimerRef.current) window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setStatus("idle");
      }, errorDuration);
    } finally {
      inFlightRef.current = false;
    }
  };

  return (
    <button
      {...buttonProps}
      type={type}
      className={`asyncActionButton ${busy ? "isBusy" : ""} ${status === "error" ? "hasError" : ""} ${compact ? "isCompact" : ""} ${className}`.trim()}
      onClick={run}
      disabled={disabled || busy}
      aria-busy={busy}
    >
      <span className="asyncActionButtonContent" aria-live="polite">
        {busy ? (
          <><span className="asyncActionSpinner" aria-hidden="true" />{!compact && <span>{busyText || t("common.processing")}</span>}</>
        ) : status === "error" ? (
          <><span className="asyncActionErrorIcon" aria-hidden="true">!</span>{!compact && <span>{errorText || t("common.actionFailed")}</span>}</>
        ) : children}
      </span>
    </button>
  );
}
